import {
  CssColor,
  CssFont,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import type { IStyleParam } from '@wix/tpa-settings';
import {
  createStylesParam,
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

import {
  BadgeLayout,
  BadgeSize,
  ProfileAlignment,
  ProfileImage,
  ProfileImageSize,
  ProfileLayout,
  ProfileWidgetHeight,
} from '../../types';
import {
  getBadgesCustomCssVars,
  getProfileAlignmentCustomCssVars,
  getProfileImageCustomCssVars,
} from './customCssVars';

export type IStylesParams = {
  showCoverPhoto: StyleParamType.Boolean;
  showCoverPhotoDesktop: StyleParamType.Boolean;
  showFollowButton: StyleParamType.Boolean;
  showRoleLabel: StyleParamType.Boolean;
  showCover: StyleParamType.Boolean;
  showMessageButton: StyleParamType.Boolean;
  showMessageButtonMobile: StyleParamType.Boolean;
  profileLayout: StyleParamType.Number;
  profileImageLayout: StyleParamType.Number;
  profileImageSize: StyleParamType.Number;
  profileAlignment: StyleParamType.Number;
  badgeLayout: StyleParamType.Number;
  badgeSize: StyleParamType.Number;
  badgeCornerRadius: StyleParamType.Number;
  badgeFont: StyleParamType.Font;
  textPrimaryColor: StyleParamType.Color;
  textPrimaryFont: StyleParamType.Font;
  textSecondaryColor: StyleParamType.Color;
  textSecondaryFont: StyleParamType.Font;
  memberNameResponsiveColor: StyleParamType.Color;
  memberNameResponsiveFont: StyleParamType.Font;
  memberNameMobileColor: StyleParamType.Color;
  memberNameMobileFont: StyleParamType.Font;
  titleFont: StyleParamType.Font;
  titleColor: StyleParamType.Color;
  titleResponsiveFont: StyleParamType.Font;
  titleResponsiveColor: StyleParamType.Color;
  titleMobileFont: StyleParamType.Font;
  titleMobileColor: StyleParamType.Color;
  ffTextResponsiveColor: StyleParamType.Color;
  ffTextResponsiveFont: StyleParamType.Font;
  ffTextMobileColor: StyleParamType.Color;
  ffTextMobileFont: StyleParamType.Font;
  buttonColor: StyleParamType.Color;
  buttonResponsiveColor: StyleParamType.Color;
  buttonMobileColor: StyleParamType.Color;
  buttonFont: StyleParamType.Font;
  buttonResponsiveFont: StyleParamType.Font;
  buttonMobileFont: StyleParamType.Font;
  boxColor: StyleParamType.Color;
  boxBorderWidth: StyleParamType.Number;
  boxBorderColor: StyleParamType.Color;
  coverColor: StyleParamType.Color;
  coverMobileColor: StyleParamType.Color;
  coverPhotoOpacity: StyleParamType.Number;
  coverPhotoOpacityDesktop: StyleParamType.Number;
  profileWidgetHeight: StyleParamType.Number;
  buttonFontColor: StyleParamType.Color;
  buttonResponsiveFontColor: StyleParamType.Color;
  buttonBorderColor: StyleParamType.Color;
  buttonResponsiveBorderColor: StyleParamType.Color;
  buttonBorderWidth: StyleParamType.Number;
  buttonResponsiveBorderWidth: StyleParamType.Number;
  buttonTextFontSizeMobile: StyleParamType.Number;
  coverColorDesktop: StyleParamType.Color;
  buttonCornerRadius: StyleParamType.Number;
  buttonResponsiveCornerRadius: StyleParamType.Number;
  verticalWidgetCornerRadius: StyleParamType.Number;
};

export const defaultButtonColor = 'color-8';

export const defaultProfileLayout = ProfileLayout.Card;

export const defaultProfileImageLayout = ProfileImage.Round;

export const defaultProfileAlignment = ProfileAlignment.Left;

export const defaultProfileImageSize = ProfileImageSize.Medium;

export const defaultBadgeLayout = BadgeLayout.NameAndIcon;

export const defaultBadgeSize = BadgeSize.Small;

export const defaultProfileWidgetHeight = ProfileWidgetHeight.Small;

export const defaultBadgeFontTextPreset = 'Body-M';

const defaultBadgeCornerRadius = 10;

const coverColor = createStylesParam('pw-cover-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-8'),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
  },
});

const coverColorDesktop = createStylesParam('pw-cover-color-desktop', {
  type: StyleParamType.Color,
  dangerousKeyTransformationOverride: () => 'pw-cover-color-desktop',
  getDefaultValue: ({ getStyleParamValue }) => {
    return getStyleParamValue(coverColor) ?? wixColorParam('color-8');
  },
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: {
      type: 'ref',
      ref: 'pw-cover-color',
      fallback: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
});

const coverMobileColor = createStylesParam('pw-cover-color-mobile', {
  type: StyleParamType.Color,
  dangerousKeyTransformationOverride: () => 'pw-cover-color-mobile',
  getDefaultValue: ({ getStyleParamValue }) => {
    return getStyleParamValue(coverColorDesktop) ?? wixColorParam('color-8');
  },
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: {
      type: 'ref',
      ref: 'pw-cover-color-desktop',
      fallback: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
});

const coverPhotoOpacity = createStylesParam('pw-cover-photo-opacity', {
  type: StyleParamType.Number,
  getDefaultValue: () => 60,
});

const coverPhotoOpacityDesktop = createStylesParam(
  'pw-cover-photo-opacity-desktop',
  {
    type: StyleParamType.Number,
    dangerousKeyTransformationOverride: () => 'pw-cover-photo-opacity-desktop',
    getDefaultValue: ({ getStyleParamValue }) => {
      const coverPhotoOpacityValue = getStyleParamValue(coverPhotoOpacity);
      return coverPhotoOpacityValue === undefined ? 60 : coverPhotoOpacityValue;
    },
  },
);

const buttonColor = createStylesParam('button-opacity-and-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(defaultButtonColor),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
  },
});

const buttonBorderColor = createStylesParam('pw-button-border-color', {
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(buttonColor) ?? wixColorParam(defaultButtonColor),
  metadata: {
    type: StyleType.borderColor,
    defaultValue: {
      type: 'ref',
      ref: 'button-opacity-and-color',
      fallback: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
});

const buttonResponsiveColor = createStylesParam('pw-responsive-button-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-1'),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
  },
});

const buttonResponsiveBorderColor = createStylesParam(
  'pw-button-responsive-border-color',
  {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(buttonResponsiveColor) ??
      wixColorParam(defaultButtonColor),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'ref',
        ref: 'pw-responsive-button-color',
        fallback: wixColor(CssColor.WstLinksAndActionsColor),
      },
    },
  },
);

const buttonMobileFont = createStylesParam('pw-button-font-mobile', {
  type: StyleParamType.Font,
  dangerousKeyTransformationOverride: () => 'pw-button-font-mobile',
  getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(CssFont.WstParagraph2Font),
  },
});

const buttonTextFontSizeMobile = createStylesParam(
  'pw-button-text-font-size-mobile',
  {
    type: StyleParamType.Number,
    dangerousKeyTransformationOverride: () => 'pw-button-text-font-size-mobile',
    getDefaultValue: ({ getStyleParamValue }) => {
      return getStyleParamValue(buttonMobileFont as any)?.size || 16;
    },
    metadata: {
      type: StyleType.fontSize,
      defaultValue: {
        type: 'ref',
        ref: 'pw-button-font-mobile',
        fallback: {
          type: 'static',
          value: 16,
        },
      },
    },
  },
);

const booleanStylesParam = ({
  isMigrated = true,
  defaultValue = true,
  customKey,
}: {
  isMigrated?: boolean;
  defaultValue?: boolean;
  customKey?: string;
} = {}): IStyleParam<StyleParamType.Boolean> => ({
  type: StyleParamType.Boolean,
  key: customKey,
  dangerousKeyTransformationOverride: customKey ? () => customKey : undefined,
  getDefaultValue: isMigrated
    ? () => undefined as unknown as boolean
    : () => defaultValue,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  },
});

const showCoverPhoto = createStylesParam('showCoverPhoto', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => undefined as unknown as boolean,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const showCoverPhotoDesktop = createStylesParam('pw-show-cover-photo-desktop', {
  type: StyleParamType.Boolean,
  dangerousKeyTransformationOverride: () => 'pw-show-cover-photo-desktop',
  getDefaultValue: () => undefined as unknown as boolean,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'ref',
      ref: 'showCoverPhoto',
      fallback: {
        type: 'static',
        value: true,
      },
    },
  },
});

export default createStylesParams<IStylesParams>({
  showCoverPhoto,
  showCoverPhotoDesktop,
  showFollowButton: booleanStylesParam(),
  showRoleLabel: booleanStylesParam(),
  showCover: booleanStylesParam({
    defaultValue: false,
    isMigrated: false,
  }),
  showMessageButton: booleanStylesParam({
    isMigrated: false,
  }),
  showMessageButtonMobile: booleanStylesParam({
    isMigrated: false,
  }),
  profileLayout: {
    type: StyleParamType.Number,
    key: 'profileLayout',
    getDefaultValue: () => defaultProfileLayout,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the layout of the profile card. 0 - Card layout, 1 - Full Width layout',
      },
      defaultValue: {
        type: 'static',
        value: defaultProfileLayout,
      },
    },
  },
  profileImageLayout: {
    type: StyleParamType.Number,
    key: 'pictureStyle',
    getDefaultValue: () => defaultProfileImageLayout,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the layout of the profile image. 0 - Round image, 1 - Square image, 2 - No image',
      },
      defaultValue: {
        type: 'static',
        value: defaultProfileImageLayout,
      },
    },
  },
  profileAlignment: {
    type: StyleParamType.Number,
    key: 'profileAlignment',
    getDefaultValue: () => defaultProfileAlignment,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the alignment of the profile image. 0 - Left alignment, 1 - Center alignment, 2 - Right alignment',
      },
      defaultValue: {
        type: 'static',
        value: defaultProfileAlignment,
      },
    },
  },
  profileImageSize: {
    type: StyleParamType.Number,
    key: 'profileImageSize',
    getDefaultValue: () => defaultProfileImageSize,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the size of the profile image. 0 - Small size, 1 - Medium size, 2 - Large size',
      },
      defaultValue: {
        type: 'static',
        value: defaultProfileImageSize,
      },
    },
  },
  badgeLayout: {
    type: StyleParamType.Number,
    key: 'badge-layout',
    getDefaultValue: () => defaultBadgeLayout,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the layout of the badge. 0 - Name and Icon, 1 - Icon Only, 2 - Name Only',
      },
      defaultValue: {
        type: 'static',
        value: defaultBadgeLayout,
      },
    },
  },
  badgeSize: {
    type: StyleParamType.Number,
    key: 'badge-size',
    getDefaultValue: () => defaultBadgeSize,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the size of the badge. 0 - Small size, 1 - Medium size, 2 - Large size',
      },
      defaultValue: {
        type: 'static',
        value: defaultBadgeSize,
      },
    },
  },
  badgeCornerRadius: {
    type: StyleParamType.Number,
    key: 'badge-corner-radius',
    getDefaultValue: () => defaultBadgeCornerRadius,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: defaultBadgeCornerRadius,
      },
    },
  },
  badgeFont: {
    type: StyleParamType.Font,
    key: 'badge-font',
    getDefaultValue: wixFontParam(defaultBadgeFontTextPreset),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  textPrimaryColor: {
    type: StyleParamType.Color,
    key: 'text-color-primary',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  textPrimaryFont: {
    type: StyleParamType.Font,
    key: 'text-primary-font',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  textSecondaryColor: {
    type: StyleParamType.Color,
    key: 'text-color-secondary',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  textSecondaryFont: {
    type: StyleParamType.Font,
    key: 'text-secondary-font',
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  memberNameResponsiveColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-name-color',
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  memberNameResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-name-font',
    getDefaultValue: wixFontParam('Body-M', { size: 28 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  memberNameMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-name-color-mobile',
    dangerousKeyTransformationOverride: () => 'pw-name-color-mobile',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  memberNameMobileFont: {
    type: StyleParamType.Font,
    key: 'pw-name-font-mobile',
    dangerousKeyTransformationOverride: () => 'pw-name-font-mobile',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  titleFont: {
    type: StyleParamType.Font,
    key: 'title-font',
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  titleColor: {
    type: StyleParamType.Color,
    key: 'title-color',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  titleResponsiveFont: {
    type: StyleParamType.Font,
    key: 'title-responsive-font',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  titleResponsiveColor: {
    type: StyleParamType.Color,
    key: 'title-responsive-color',
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  titleMobileFont: {
    type: StyleParamType.Font,
    key: 'title-mobile-font',
    dangerousKeyTransformationOverride: () => 'title-mobile-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  titleMobileColor: {
    type: StyleParamType.Color,
    key: 'title-mobile-color',
    dangerousKeyTransformationOverride: () => 'title-mobile-color',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  ffTextResponsiveColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-ff-color',
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  ffTextResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-ff-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  ffTextMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-ff-color-mobile',
    dangerousKeyTransformationOverride: () => 'pw-ff-color-mobile',
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  ffTextMobileFont: {
    type: StyleParamType.Font,
    key: 'pw-ff-font-mobile',
    dangerousKeyTransformationOverride: () => 'pw-ff-font-mobile',
    getDefaultValue: wixFontParam('Body-M', { size: 12 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  buttonColor,
  buttonResponsiveColor,
  buttonMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-button-color-mobile',
    dangerousKeyTransformationOverride: () => {
      return 'pw-responsive-button-color-mobile';
    },
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  buttonFont: {
    type: StyleParamType.Font,
    key: 'button-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  buttonResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-button-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  buttonMobileFont,
  boxColor: {
    type: StyleParamType.Color,
    key: 'box-color',
    dangerousKeyTransformationOverride: () => 'box-color',
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  boxBorderWidth: {
    type: StyleParamType.Number,
    key: 'border-width-1',
    dangerousKeyTransformationOverride: () => 'border-width-1',
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  boxBorderColor: {
    type: StyleParamType.Color,
    key: 'border-color',
    dangerousKeyTransformationOverride: () => 'border-color',
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color, 0.2),
    },
  },
  coverColor,
  coverColorDesktop,
  coverMobileColor,
  coverPhotoOpacity,
  coverPhotoOpacityDesktop,
  profileWidgetHeight: {
    type: StyleParamType.Number,
    key: 'profileWidgetHeight',
    getDefaultValue: () => defaultProfileWidgetHeight,
    metadata: {
      type: StyleType.numberEnum,
      ai: {
        description:
          'Controls the height of the profile widget. 250 - Small height, 340 - Medium height, 430 - Large height',
      },
      defaultValue: {
        type: 'static',
        value: defaultProfileWidgetHeight,
      },
    },
  },
  buttonFontColor: {
    type: StyleParamType.Color,
    key: 'pw-button-font-color',
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  buttonResponsiveFontColor: {
    type: StyleParamType.Color,
    key: 'pw-button-responsive-font-color',
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  buttonBorderColor,
  buttonResponsiveBorderColor,
  buttonBorderWidth: {
    type: StyleParamType.Number,
    key: 'pw-button-border-width',
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  buttonResponsiveBorderWidth: {
    type: StyleParamType.Number,
    key: 'pw-button-responsive-border-width',
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  buttonTextFontSizeMobile,
  buttonCornerRadius: {
    key: 'pw-button-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonResponsiveCornerRadius: {
    key: 'pw-button-responsive-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  verticalWidgetCornerRadius: {
    key: 'vertical-pw-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
});

export const customCssVars: CustomCssVarsFn = (props) => {
  const { styleParams } = props;
  const { numbers } = styleParams;

  const profileImageCustomCssVars = getProfileImageCustomCssVars(props);
  const badgesCustomCssVars = getBadgesCustomCssVars(props);
  const profileAlignmentCustomCssVars = getProfileAlignmentCustomCssVars(props);

  return {
    profileWidgetHeight: `${numbers.profileWidgetHeight}px`,
    ...profileImageCustomCssVars,
    ...profileAlignmentCustomCssVars,
    ...badgesCustomCssVars,
  };
};
